import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PagyMetadata } from "@/types";

interface DataTablePaginationProps {
  pagy: PagyMetadata;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  entityName: string; // e.g., "productos", "proveedores", "solicitudes"
}

/**
 * Reusable server-side pagination component for data tables.
 * Works with Rails Pagy gem pagination metadata.
 */
export function DataTablePagination({
  pagy,
  isLoading = false,
  onPageChange,
  entityName,
}: DataTablePaginationProps) {
  return (
    <div className="flex items-center justify-between px-2">
      <div className="text-sm text-muted-foreground">
        Mostrando {pagy.from} a {pagy.to} de {pagy.count} {entityName}
      </div>

      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(pagy.prev!)}
          disabled={!pagy.prev || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </Button>

        <div className="flex items-center gap-1">
          {pagy.series.map((seriesItem, index) => {
            if (seriesItem === "gap") {
              return (
                <span key={`gap-${index}`} className="px-2">
                  ...
                </span>
              );
            }

            const pageNum =
              typeof seriesItem === "number"
                ? seriesItem
                : Number.parseInt(seriesItem, 10);

            if (Number.isNaN(pageNum)) {
              return null;
            }

            const isActive = pageNum === pagy.page;

            const pageButtonClassName = cn(
              "w-8 transition-all",
              isActive && "shadow-lg ring-2 ring-primary/80 disabled:opacity-100 font-semibold"
            );

            return (
              <Button
                key={`page-${index}-${seriesItem}`}
                variant={isActive ? "fancy" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                disabled={isActive || isLoading}
                aria-current={isActive ? "page" : undefined}
                className={pageButtonClassName}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(pagy.next!)}
          disabled={!pagy.next || isLoading}
        >
          Siguiente
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
